<template>
  <div class="flex">
    <div class="group relative flex cursor-pointer items-center">
      <slot name="text"></slot>
      <div :class="$slots.trigger ? 'group' : 'information group'">
        <div ref="reference" @click.prevent.stop="">
          <slot name="trigger">
            <InformationCircleIcon class="h-5 w-5" />
          </slot>
        </div>
        <Transition
          enter-active-class="information-enter-active"
          leave-active-class="information-leave-active"
          enter-from-class="information-enter-from"
          leave-to-class="information-leave-to"
        >
          <div
            v-if="isHovered"
            ref="floating"
            :style="floatingStyles"
            class="carvia-modal-outer fixed z-[9999]"
          >
            <div
              :style="{ transformOrigin: transformOrigin }"
              class="carvia-modal information-inner"
            >
              <div ref="arrowRef" :style="arrowStyles" class="information-arrow" aria-hidden />
              <slot name="modal"></slot>
            </div>
          </div>
        </Transition>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { InformationCircleIcon } from '@heroicons/vue/24/outline';
import { computed, ref } from 'vue';
import { arrow, offset, useFloating, autoUpdate, flip, shift } from '@floating-ui/vue';
import { useElementHover } from '@vueuse/core';

const reference = ref(null);
const floating = ref(null);
const arrowRef = ref(null);

const { floatingStyles, middlewareData, placement } = useFloating(reference, floating, {
  placement: 'top',
  middleware: [offset(10), flip(), shift({ padding: 8 }), arrow({ element: arrowRef, padding: 8 })],
  whileElementsMounted: autoUpdate,
});

const isHovered = useElementHover(reference);
const transformOrigin = computed(() => {
  const side = placement.value?.split('-')[0] ?? 'top';
  const map: Record<string, string> = {
    top: 'bottom center',
    bottom: 'top center',
    left: 'right center',
    right: 'left center',
  };
  return map[side] ?? 'bottom center';
});

const arrowStyles = computed(() => {
  const data = middlewareData.value?.arrow;
  const side = placement.value?.split('-')[0] ?? 'top';
  const base: Record<string, string> = {
    marginLeft: '-4px',
    marginTop: '-4px',
    // Only rotate when arrow is above the tooltip (placement bottom) so point faces up
    transform: side === 'bottom' ? 'rotate(225deg)' : 'rotate(45deg)',
  };
  // For top/bottom placements middleware sets x only; for left/right it sets y only.
  // We set the static axis so the arrow sits on the correct edge.
  switch (side) {
    case 'top':
      base.top = '100%';
      base.left = data?.x != null ? `${data.x}px` : '50%';
      break;
    case 'bottom':
      base.top = '0';
      base.left = data?.x != null ? `${data.x}px` : '50%';
      break;
    case 'left':
      base.left = '100%';
      base.top = data?.y != null ? `${data.y}px` : '50%';
      break;
    case 'right':
      base.left = '0';
      base.top = data?.y != null ? `${data.y}px` : '50%';
      break;
    default:
      base.left = data?.x != null ? `${data.x}px` : '50%';
      base.top = data?.y != null ? `${data.y}px` : '50%';
  }
  return base;
});
</script>
<style scoped>
.information {
  @apply relative ml-2 cursor-pointer select-none;
}

.carvia-modal {
  @apply z-50 w-max max-w-[400px] space-y-1 rounded-md bg-white p-3 text-center text-sm font-normal text-black shadow;
}

.information-arrow {
  position: absolute;
  width: 8px;
  height: 8px;
  background: white;
  box-shadow: 1px 1px 0 0 rgba(0, 0, 0, 0.05);
}

/* Fade on outer (keeps floating position); scale on inner only */
.information-enter-active,
.information-leave-active {
  transition: opacity 0.1s ease-out;
}

.information-enter-active .information-inner,
.information-leave-active .information-inner {
  transition: transform 0.1s ease-out;
}

.information-enter-from,
.information-leave-to {
  opacity: 0;
}

.information-enter-from .information-inner,
.information-leave-to .information-inner {
  transform: scale(0.95);
}
</style>
